import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import carrito from "../../../assets/image/las-compras-en-linea 1.svg";
import "../../success/success-minimal.css";
import { MAIN_API } from "../../../api/apilist";
import { getEventById, getOrderStatusApi } from "../../../api/api";
import {
	buildMetaPurchasePayload,
	getResolvedMetaPixelId,
	loadMetaPixel,
	trackMetaPixel,
} from "../../../helpers/metaPixel";

const POLL_INTERVAL_MS = 2500;
const POLL_MAX_ATTEMPTS = 8;

export default function SuccessFiservPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const user = useSelector((s) => s.auth?.user);
	const accessToken = useSelector((s) => s.auth?.accessToken);
	const [allowRedirect, setAllowRedirect] = useState(false);
	const [statusCopy, setStatusCopy] = useState({
		title: "Recibimos tu pago",
		detail: "Estamos confirmando tu compra y preparando tus entradas.",
	});
	const purchaseTrackedRef = useRef(false);

	const { orderId, status, isReservation } = useMemo(() => {
		const sp = new URLSearchParams(location.search || "");
		const resolvedOrderId =
			sp.get("orderId") ||
			sp.get("orderID") ||
			sp.get("order") ||
			sp.get("oid") ||
			"";
		const rawStatus = (sp.get("status") || "").toLowerCase();
		const isReservation =
			(sp.get("type") || "").toLowerCase() === "reservation";
		const normalizedStatus = (() => {
			if (!rawStatus) return "paid";
			if (
				["paid", "success", "approved", "ok"].some((kw) =>
					rawStatus.includes(kw),
				)
			)
				return "paid";
			if (
				[
					"fail",
					"failed",
					"error",
					"denied",
					"declined",
					"cancel",
					"canceled",
					"cancelled",
					"pending",
				].some((kw) => rawStatus.includes(kw))
			) {
				return "failed";
			}
			return rawStatus;
		})();
		return {
			orderId: resolvedOrderId,
			status: normalizedStatus,
			isReservation,
		};
	}, [location.search]);

	const isMobile = useMemo(() => {
		if (typeof navigator === "undefined") return false;
		return /android|iphone|ipad|ipod/i.test(navigator.userAgent || "");
	}, []);

	const deepLinkUrl = useMemo(() => {
		const base = "zoco-tickets://payment/success";
		if (!orderId) return base;
		return `${base}?orderId=${encodeURIComponent(orderId)}`;
	}, [orderId]);

	const reservationReturn = useMemo(() => {
		try {
			const raw = sessionStorage.getItem("reservation:last");
			const parsed = raw ? JSON.parse(raw) : null;
			if (
				!["reservas", "canchas"].includes(parsed?.flow) ||
				!parsed?.reservationCode ||
				!orderId
			)
				return null;
			if (String(parsed.reservationCode) !== String(orderId)) return null;
			return parsed;
		} catch (_) {
			return null;
		}
	}, [orderId]);

	const trackPurchaseIfReady = async (orderInfo) => {
		if (
			purchaseTrackedRef.current ||
			!orderInfo?.isReady ||
			!orderInfo?.eventId
		)
			return;
		const storageKey = `zoco-meta-purchase:${orderInfo.orderId || orderId || ""}`;
		try {
			if (storageKey && localStorage.getItem(storageKey)) {
				purchaseTrackedRef.current = true;
				return;
			}
		} catch (_) {}

		try {
			const eventRes = await getEventById(orderInfo.eventId);
			const eventInfo = eventRes?.data?.info?.event || {};
			const pixelId = getResolvedMetaPixelId(eventInfo);
			if (!loadMetaPixel(pixelId)) return;

			const { params, options } = buildMetaPurchasePayload(orderInfo);
			trackMetaPixel("Purchase", params, options);
			purchaseTrackedRef.current = true;
			try {
				if (storageKey) localStorage.setItem(storageKey, "1");
			} catch (_) {}
		} catch (_) {}
	};

	useEffect(() => {
		if (isReservation) {
			const target =
				status === "paid" ? "/reservas/success" : "/reservas/cancel";
			navigate(`${target}${location.search || ""}`, { replace: true });
			return;
		}
		if (reservationReturn) {
			const base =
				reservationReturn.flow === "canchas" ? "/canchas" : "/reservas";
			const next =
				status === "paid"
					? `${base}/success?code=${encodeURIComponent(String(reservationReturn.reservationCode))}`
					: `${base}/cancel${location.search || ""}`;
			navigate(next, { replace: true });
			return;
		}
		if (status !== "paid") {
			if (status) {
				navigate(`/payment/fiserv/cancel${location.search || ""}`, {
					replace: true,
				});
			}
			return;
		}
	}, [isReservation, reservationReturn, status, location.search, navigate]);

	useEffect(() => {
		if (isReservation || status !== "paid" || !orderId) return;
		(async () => {
			try {
				const params = new URLSearchParams();
				params.set("oid", orderId);
				params.set("status", "paid");
				params.set("respcode", "00");
				params.set("approval_code", "FRONT_OK");
				const url = `${MAIN_API}/api/payment/fiserv/connect/return?${params.toString()}`;
				await fetch(url, { method: "GET" });
				try {
					localStorage.setItem("lastOrderId", orderId);
				} catch (_) {}
			} catch (_) {}
		})();
	}, [isReservation, orderId, status]);

	useEffect(() => {
		if (isReservation || status !== "paid") return;
		if (!orderId || !accessToken) {
			setAllowRedirect(true);
			return;
		}

		let cancelled = false;

		const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

		(async () => {
			for (
				let attempt = 0;
				attempt < POLL_MAX_ATTEMPTS && !cancelled;
				attempt += 1
			) {
				try {
					const res = await getOrderStatusApi(accessToken, orderId);
					const info = res?.data?.info || {};

					if (info?.isReady) {
						await trackPurchaseIfReady(info);
						setStatusCopy({
							title: "Tus entradas ya estan listas",
							detail: "Ya podes entrar a Mis eventos para ver tus tickets.",
						});
						setAllowRedirect(true);
						return;
					}

					if (info?.paymentstatus === "paid") {
						setStatusCopy({
							title: "Recibimos tu pago",
							detail:
								"Estamos emitiendo tus entradas. Esto puede tardar unos segundos.",
						});
					} else {
						setStatusCopy({
							title: "Estamos confirmando tu compra",
							detail:
								"Todavia no terminamos de validar el pago. Espera unos segundos.",
						});
					}
				} catch (_) {
					setStatusCopy({
						title: "Estamos confirmando tu compra",
						detail: "Estamos chequeando el estado de tu orden.",
					});
				}

				await sleep(POLL_INTERVAL_MS);
			}

			if (!cancelled) {
				setStatusCopy({
					title: "Recibimos tu pago",
					detail:
						"Si tus entradas no aparecen enseguida, entra a Mis eventos en unos segundos.",
				});
				setAllowRedirect(true);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [accessToken, isReservation, orderId, status]);

	useEffect(() => {
		if (isReservation || status !== "paid" || !allowRedirect || isMobile)
			return;
		const t = setTimeout(() => navigate("/myevents", { replace: true }), 3000);
		return () => clearTimeout(t);
	}, [allowRedirect, isReservation, navigate, status, isMobile]);

	useEffect(() => {
		if (isReservation || status !== "paid" || !allowRedirect || !isMobile)
			return;
		const t = setTimeout(() => {
			try {
				window.location.href = deepLinkUrl;
			} catch (_) {}
		}, 350);
		return () => clearTimeout(t);
	}, [allowRedirect, deepLinkUrl, isReservation, status, isMobile]);

	if (isReservation) return null;

	const firstName = (user?.name || "").split(" ")[0] || "";

	return (
		<div className="success-minimal">
			<div className="success-minimal-card">
				<img className="success-minimal-icon" src={carrito} alt="abrir" />
				<h2 className="success-minimal-title">
					{statusCopy.title}
					{firstName ? `, ${firstName}` : ""}
				</h2>
				<p className="success-minimal-subtext">
					{isMobile ? (
						`${statusCopy.detail} Si no se abre la app, usa el boton de abajo.`
					) : (
						<>
							{statusCopy.detail} En unos segundos te vamos a redirigir a{" "}
							<strong>Mis eventos</strong>.
						</>
					)}
				</p>
				{orderId ? (
					<p className="success-minimal-subtext" style={{ marginTop: -4 }}>
						Orden: <strong>{orderId}</strong>
					</p>
				) : null}
				<button
					className="success-minimal-btn"
					onClick={() => navigate("/myevents")}
				>
					{isMobile ? "Seguir en la web" : "Ir ahora"}
				</button>
				{isMobile ? (
					<button
						className="success-minimal-btn"
						onClick={() => {
							try {
								window.location.href = deepLinkUrl;
							} catch (_) {}
						}}
					>
						Abrir en la app
					</button>
				) : null}
				<div className="success-minimal-note">
					<span>
						<i>i</i> Tu QR es dinamico y se actualiza automaticamente. Si el
						mail tarda, vas a seguir viendo tus entradas desde Mis eventos.
					</span>
				</div>
			</div>
		</div>
	);
}
